import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Sidebar } from '../dashboard_component';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { cbtAPI, labelAPI } from '../../api';

const CbtList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialLabelId = searchParams.get('label_id');
  
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [cbtList, setCbtList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLabelId, setFilterLabelId] = useState(initialLabelId || '');
  const [availableLabels, setAvailableLabels] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingCbtId, setDeletingCbtId] = useState(null);
  const [deletingCbtName, setDeletingCbtName] = useState('');

  const handleToggle = () => setIsCollapsed(!isCollapsed);

  // Fetch labels
  const fetchLabels = async () => {
    try {
      const res = await labelAPI.getLabels();
      setAvailableLabels(res.data || []);
    } catch (error) {
      console.error('Failed to load labels', error);
    }
  };

  // Fetch CBTs with optional label filter
  const fetchCbts = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterLabelId) params.label_id = filterLabelId;
      const response = await cbtAPI.getMyCBTs(params);
      if (response.success && Array.isArray(response.data)) {
        setCbtList(response.data);
      } else {
        setCbtList([]);
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Failed to fetch CBTs');
      setCbtList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLabels();
  }, []);

  useEffect(() => {
    fetchCbts();
  }, [filterLabelId]);

  // Filter by search term (client-side)
  const filteredCbtList = cbtList.filter(cbt =>
    cbt.cbt_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cbt.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDelete = async () => {
    if (!deletingCbtId) return;
    try {
      const response = await cbtAPI.deleteCBT(deletingCbtId);
      if (response.success) {
        toast.success('CBT deleted successfully');
        fetchCbts();
      } else {
        toast.error(response.error || 'Failed to delete CBT');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete CBT');
    } finally {
      setShowDeleteModal(false);
      setDeletingCbtId(null);
    }
  };

  const openDeleteModal = (id, name) => {
    setDeletingCbtId(id);
    setDeletingCbtName(name);
    setShowDeleteModal(true);
  };

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar isCollapsed={isCollapsed} onToggle={handleToggle} />
      <div className={`flex-1 transition-all duration-300 pt-16 lg:pt-8 ${isCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">CBT Library</h1>
              <p className="text-gray-600 mt-1">Manage your computer-based tests by folders</p>
            </div>
            <button
              onClick={() => navigate('/cbt_creator/cbt-create')}
              className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all flex items-center gap-2 shadow-md"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New CBT
            </button>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <input
                  type="text"
                  placeholder="Search by name or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="sm:w-64">
                <label className="block text-sm font-medium text-gray-700 mb-1">Folder</label>
                <select
                  value={filterLabelId}
                  onChange={(e) => setFilterLabelId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">All Folders ({filteredCbtList.length || 0 })</option>
                  {availableLabels.map(label => (
                    <option key={label.label_id} value={label.label_id}>
                      {label.name} ({label.cbt_count || 0})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* CBT Grid */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredCbtList.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <div className="text-6xl mb-4">📂</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                {searchTerm || filterLabelId ? 'No CBTs found' : 'No CBTs created yet'}
              </h3>
              <p className="text-gray-500 mb-6">
                {searchTerm || filterLabelId
                  ? 'Try changing your search or folder filter.'
                  : 'Get started by creating your first computer-based test.'}
              </p>
              {!searchTerm && !filterLabelId && (
                <button
                  onClick={() => navigate('/cbt_creator/cbt-create')}
                  className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Your First CBT
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCbtList.map((cbt) => (
                <div key={cbt.cbt_data_id} className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all overflow-hidden">
                  <div className="p-5">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 text-lg line-clamp-2">{cbt.cbt_name}</h3>
                        <p className="text-xs text-gray-500 mt-1">ID: {cbt.cbt_data_id}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        cbt.cbt_status === 'published' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {cbt.cbt_status}
                      </span>
                    </div>
                    <div className="space-y-2 text-sm text-gray-600 mb-4">
                      <div className="flex justify-between">
                        <span>Access:</span>
                        <span className="font-medium">{cbt.access_type === 'open_access' ? 'Open' : 'Restricted'}</span>
                      </div>
                      {cbt.cbt_duration > 0 && (
                        <div className="flex justify-between">
                          <span>Duration:</span>
                          <span className="font-medium">{cbt.cbt_duration} min</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span>Created:</span>
                        <span className="font-medium">{new Date(cbt.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button 
                            className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-1 text-sm font-medium"
                            onClick={() => navigate(`/cbt_creator/cbt_data?cbt_data_id=${cbt.cbt_data_id}`)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {/* Manage */}
                        </button>
                      <button
                        onClick={() => navigate(`/cbt_creator/cbt-edit/${cbt.cbt_data_id}`)}
                        className="flex-1 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium flex items-center justify-center gap-1"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </button>
                      <button
                        onClick={() => openDeleteModal(cbt.cbt_data_id, cbt.cbt_name)}
                        className="px-3 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="bg-red-600 text-white px-6 py-4 rounded-t-2xl">
              <h3 className="text-xl font-bold">Confirm Deletion</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-4">Are you sure you want to delete <strong>{deletingCbtName}</strong>? This action cannot be undone.</p>
              <div className="flex justify-end space-x-3">
                <button onClick={() => setShowDeleteModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">
                  Cancel
                </button>
                <button onClick={handleDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default CbtList;